import type { ReactNode } from 'react'

import { ApiError } from '../lib/api'

/**
 * Pecas de interface construidas SOBRE o gov.br DS.
 *
 * Sao wrappers finos: a aparencia vem do design system (br-input, br-button,
 * br-message, br-checkbox) mais a camada `ifr-*` de theme/design.css. O que
 * estes componentes acrescentam e o comportamento de acessibilidade que o DS
 * deixa a cargo de quem usa — vinculo de rotulo, aria-invalid, aria-describedby
 * e aria-live.
 *
 * NENHUM `style={{}}` de layout aqui, de proposito. A regra da camada de
 * design: inline apenas para valor calculado em tempo de execucao (a largura de
 * uma barra de proporcao), nunca para espacamento, cor ou tipografia — para
 * isso existem as escalas do DS.
 */

// ─────────────────────────────────────────────────────────────────── campo

interface CampoProps {
  id: string
  rotulo: string
  tipo?: string
  valor: string
  aoMudar: (v: string) => void
  erros?: string[]
  dica?: string
  obrigatorio?: boolean
  autoComplete?: string
  inputMode?: 'text' | 'email' | 'tel' | 'numeric' | 'decimal' | 'search' | 'url'
  placeholder?: string
  autoFocus?: boolean
  maxLength?: number
}

/** Marca de campo obrigatorio. O asterisco e decorativo; `aria-required` informa. */
export function Obrigatorio() {
  return (
    <span aria-hidden="true" className="ifr-obrigatorio">
      {' '}
      *
    </span>
  )
}

export function Campo({
  id,
  rotulo,
  tipo = 'text',
  valor,
  aoMudar,
  erros,
  dica,
  obrigatorio,
  autoComplete,
  inputMode,
  placeholder,
  autoFocus,
  maxLength,
}: CampoProps) {
  const temErro = Boolean(erros?.length)
  const idDica = dica ? `${id}-dica` : undefined
  const idErro = temErro ? `${id}-erro` : undefined

  return (
    <div className={`br-input ${temErro ? 'danger' : ''}`}>
      <label htmlFor={id}>
        {rotulo}
        {obrigatorio && <Obrigatorio />}
      </label>
      <input
        id={id}
        type={tipo}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        // aria-describedby aponta para dica E erro: o leitor de tela anuncia os
        // dois, e não só o último.
        aria-describedby={[idDica, idErro].filter(Boolean).join(' ') || undefined}
        aria-invalid={temErro || undefined}
        aria-required={obrigatorio || undefined}
        autoComplete={autoComplete}
        inputMode={inputMode}
        placeholder={placeholder}
        autoFocus={autoFocus}
        maxLength={maxLength}
      />
      {dica && !temErro && (
        <span id={idDica} className="feedback ifr-fraco">
          {dica}
        </span>
      )}
      {temErro && (
        <span id={idErro} className="feedback danger" role="alert">
          {erros!.join(' ')}
        </span>
      )}
    </div>
  )
}

/** Campo de selecao, com o mesmo contrato de acessibilidade do Campo. */
export function Selecao({
  id,
  rotulo,
  valor,
  aoMudar,
  opcoes,
  dica,
  erros,
  obrigatorio,
}: {
  id: string
  rotulo: string
  valor: string
  aoMudar: (v: string) => void
  opcoes: ReadonlyArray<{ valor: string; rotulo: string }>
  dica?: string
  erros?: string[]
  obrigatorio?: boolean
}) {
  const temErro = Boolean(erros?.length)
  return (
    <div className={`br-select ${temErro ? 'danger' : ''}`}>
      <label htmlFor={id}>
        {rotulo}
        {obrigatorio && <Obrigatorio />}
      </label>
      <select
        id={id}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        aria-invalid={temErro || undefined}
        aria-describedby={temErro ? `${id}-erro` : dica ? `${id}-dica` : undefined}
      >
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.rotulo}
          </option>
        ))}
      </select>
      {dica && !temErro && (
        <span id={`${id}-dica`} className="feedback ifr-fraco">
          {dica}
        </span>
      )}
      {temErro && (
        <span id={`${id}-erro`} className="feedback danger" role="alert">
          {erros!.join(' ')}
        </span>
      )}
    </div>
  )
}

/** Caixa de marcacao. */
export function Marcar({
  id,
  rotulo,
  marcado,
  aoMudar,
  erros,
}: {
  id: string
  rotulo: ReactNode
  marcado: boolean
  aoMudar: (v: boolean) => void
  erros?: string[]
}) {
  const temErro = Boolean(erros?.length)
  return (
    <div className={`br-checkbox ${temErro ? 'danger' : ''}`}>
      <input
        id={id}
        type="checkbox"
        checked={marcado}
        onChange={(e) => aoMudar(e.target.checked)}
        aria-invalid={temErro || undefined}
        aria-describedby={temErro ? `${id}-erro` : undefined}
      />
      <label htmlFor={id}>{rotulo}</label>
      {temErro && (
        <span id={`${id}-erro`} className="feedback danger" role="alert">
          {erros!.join(' ')}
        </span>
      )}
    </div>
  )
}

/** Controle de faixa, com o valor no proprio rotulo. */
export function Faixa({
  id,
  rotulo,
  valor,
  aoMudar,
  minimo,
  maximo,
  passo = 1,
  unidade = '',
  dica,
}: {
  id: string
  rotulo: string
  valor: number
  aoMudar: (v: number) => void
  minimo: number
  maximo: number
  passo?: number
  unidade?: string
  dica?: string
}) {
  return (
    <div className="br-input">
      <label htmlFor={id}>
        {rotulo}: <strong>{valor.toLocaleString('pt-BR')}{unidade}</strong>
      </label>
      <input
        id={id}
        type="range"
        min={minimo}
        max={maximo}
        step={passo}
        value={valor}
        onChange={(e) => aoMudar(Number(e.target.value))}
        // O leitor de tela anunciaria só o número cru; aria-valuetext dá a
        // unidade, que é o que torna o valor compreensível.
        aria-valuetext={`${valor} ${unidade || ''}`.trim()}
      />
      {dica && <span className="feedback ifr-fraco">{dica}</span>}
    </div>
  )
}

// ───────────────────────────────────────────────────────────────── mensagem

type TipoMensagem = 'success' | 'danger' | 'warning' | 'info'

const ICONE_MENSAGEM: Record<TipoMensagem, string> = {
  success: 'fa-check-circle',
  danger: 'fa-times-circle',
  warning: 'fa-exclamation-triangle',
  info: 'fa-info-circle',
}

export function Mensagem({
  tipo,
  titulo,
  children,
}: {
  tipo: TipoMensagem
  titulo?: string
  children: ReactNode
}) {
  return (
    // role=alert em erro (interrompe o leitor); role=status no resto (aguarda a
    // pausa). Usar alert para tudo transforma confirmação em interrupção.
    <div
      className={`br-message ${tipo}`}
      role={tipo === 'danger' ? 'alert' : 'status'}
      aria-live={tipo === 'danger' ? 'assertive' : 'polite'}
    >
      <div className="icon">
        <i className={`fas ${ICONE_MENSAGEM[tipo]}`} aria-hidden="true" />
      </div>
      <div className="content">
        {titulo && <strong>{titulo}</strong>}
        <div>{children}</div>
      </div>
    </div>
  )
}

/**
 * Traduz um ApiError em mensagem de topo, sem repetir os erros de campo.
 *
 * Cuida do proprio estado vazio E do proprio espacamento, de proposito: assim o
 * uso e sempre `<ErroApi erro={erro} />`, sem `{erro && ...}` no chamador. Alem
 * de encurtar o codigo, isso evita um erro de tipo real — `erro` e `unknown`, e
 * `unknown && JSX` produz `unknown`, que nao e um ReactNode valido.
 */
export function ErroApi({ erro }: { erro: unknown }): React.ReactElement | null {
  if (!erro) return null

  if (erro instanceof ApiError) {
    // Erro de validação já aparece campo a campo: repetir tudo aqui só
    // duplicaria o ruído.
    if (erro.status === 422 && Object.keys(erro.camposComErro).length > 0) {
      return (
        <Mensagem tipo="danger" titulo="Confira os campos">
          Alguns campos precisam de correção.
        </Mensagem>
      )
    }
    return (
      <Mensagem tipo="danger" titulo={erro.problem.title}>
        {erro.problem.detail ?? 'Tente novamente.'}
      </Mensagem>
    )
  }

  return (
    <Mensagem tipo="danger" titulo="Falha de conexão">
      Não conseguimos falar com o servidor. Verifique sua internet e tente de novo.
    </Mensagem>
  )
}

// ─────────────────────────────────────────────────────────────────── botão

export function Botao({
  children,
  tipo = 'button',
  variante = 'primary',
  carregando,
  desabilitado,
  aoClicar,
  larguraTotal,
  pequeno,
  rotuloAcessivel,
}: {
  children: ReactNode
  tipo?: 'button' | 'submit'
  variante?: 'primary' | 'secondary' | 'tertiary'
  carregando?: boolean
  desabilitado?: boolean
  aoClicar?: () => void
  larguraTotal?: boolean
  pequeno?: boolean
  rotuloAcessivel?: string
}) {
  return (
    <button
      type={tipo}
      className={['br-button', variante, larguraTotal ? 'block' : '', pequeno ? 'small' : '']
        .filter(Boolean)
        .join(' ')}
      disabled={desabilitado || carregando}
      onClick={aoClicar}
      aria-label={rotuloAcessivel}
      // aria-busy avisa o leitor de tela que a ação está em curso; o texto
      // visível não muda, então sem isso a espera é silenciosa.
      aria-busy={carregando || undefined}
    >
      {carregando && <i className="fas fa-circle-notch fa-spin" aria-hidden="true" />}
      {carregando ? 'Aguarde…' : children}
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────── cartão

/** Cartao centralizado das telas de autenticacao. */
export function Cartao({
  titulo,
  descricao,
  children,
  largo,
}: {
  titulo: string
  descricao?: string
  children: ReactNode
  largo?: boolean
}) {
  return (
    <div className={`ifr-cartao-forma ${largo ? 'ifr-cartao-forma--largo' : ''}`}>
      <h1 className="ifr-titulo ifr-titulo--secao">{titulo}</h1>
      {descricao && <p className="ifr-apoio">{descricao}</p>}
      <div className="ifr-cartao-forma__corpo">{children}</div>
    </div>
  )
}

/** Bloco de conteudo. `variante` pinta a faixa lateral. */
export function Bloco({
  children,
  variante,
  compacto,
  semPadding,
}: {
  children: ReactNode
  variante?: 'coleta' | 'entrega' | 'atencao'
  compacto?: boolean
  semPadding?: boolean
}) {
  return (
    <div
      className={[
        'ifr-cartao',
        compacto ? 'ifr-cartao--compacto' : '',
        semPadding ? 'ifr-cartao--sem-padding' : '',
        variante ? `ifr-cartao--${variante}` : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  )
}

/** Bloco com titulo — usado nas secoes do detalhe do frete. */
export function Secao({
  titulo,
  children,
  acoes,
}: {
  titulo: string
  children: ReactNode
  acoes?: ReactNode
}) {
  return (
    <section className="ifr-cartao">
      <div className="ifr-linha ifr-linha--entre">
        <h2 className="ifr-titulo ifr-titulo--secao">{titulo}</h2>
        {acoes}
      </div>
      <div className="ifr-secao__corpo">{children}</div>
    </section>
  )
}

// ────────────────────────────────────────────────────────────── estados

export function Carregando({ rotulo = 'Carregando' }: { rotulo?: string }) {
  return (
    <div className="ifr-carregando" role="status" aria-live="polite">
      <i className="fas fa-circle-notch fa-spin fa-2x" aria-hidden="true" />
      <span>{rotulo}…</span>
    </div>
  )
}

export function Vazio({
  icone = 'fa-inbox',
  titulo,
  children,
}: {
  icone?: string
  titulo: string
  children?: ReactNode
}) {
  return (
    <div className="ifr-vazio">
      <i className={`fas ${icone} ifr-vazio__icone`} aria-hidden="true" />
      <div>
        <strong className="ifr-forte">{titulo}</strong>
        {children && <p className="ifr-apoio">{children}</p>}
      </div>
    </div>
  )
}

// ───────────────────────────────────────────────────────── lista de dados

/** Par rotulo/valor. Usar dentro de <Dados>. */
export function Dado({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="ifr-dados__item">
      <dt className="ifr-dados__rotulo">{rotulo}</dt>
      <dd className="ifr-dados__valor">{children}</dd>
    </div>
  )
}

/** `<dl>` de verdade: o leitor de tela entende a relacao rotulo/valor. */
export function Dados({ children }: { children: ReactNode }) {
  return <dl className="ifr-dados">{children}</dl>
}

// ─────────────────────────────────────────────────────────────────── tag

export function Tag({
  children,
  variante,
  icone,
}: {
  children: ReactNode
  variante?: 'marca' | 'sucesso' | 'atencao' | 'perigo'
  icone?: string
}) {
  return (
    <span className={`ifr-tag ${variante ? `ifr-tag--${variante}` : ''}`}>
      {icone && <i className={`fas ${icone}`} aria-hidden="true" />}
      {children}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────── barra

/**
 * Barra de proporcao. `valor` de 0 a 1.
 *
 * A largura e o unico `style` inline legitimo desta base de codigo: e valor
 * calculado em tempo de execucao, nao decisao de design.
 */
export function Barra({
  valor,
  variante,
  rotulo,
}: {
  valor: number
  variante?: 'sucesso' | 'atencao' | 'perigo'
  rotulo?: string
}) {
  const pct = Math.max(0, Math.min(1, valor)) * 100
  return (
    <div
      className="ifr-barra"
      role={rotulo ? 'progressbar' : undefined}
      aria-label={rotulo}
      aria-valuenow={rotulo ? Math.round(pct) : undefined}
      aria-valuemin={rotulo ? 0 : undefined}
      aria-valuemax={rotulo ? 100 : undefined}
    >
      <div
        className={`ifr-barra__preenchimento ${
          variante ? `ifr-barra__preenchimento--${variante}` : ''
        }`}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────── cabeçalho de página

export function CabecalhoPagina({
  titulo,
  apoio,
  acoes,
}: {
  titulo: ReactNode
  apoio?: ReactNode
  acoes?: ReactNode
}) {
  return (
    <header className="ifr-cabecalho">
      <div className="ifr-cabecalho__texto">
        <h1 className="ifr-titulo">{titulo}</h1>
        {apoio && <p className="ifr-apoio">{apoio}</p>}
      </div>
      {acoes && <div className="ifr-linha">{acoes}</div>}
    </header>
  )
}

/** Indicador numerico grande com rotulo — contadores e metricas. */
export function Indicador({
  valor,
  rotulo,
  variante,
}: {
  valor: number | string
  rotulo: string
  variante?: 'sucesso' | 'atencao' | 'perigo'
}) {
  return (
    <div className={`ifr-cartao ifr-indicador ${variante ? `ifr-indicador--${variante}` : ''}`}>
      <div className="ifr-indicador__valor">{valor}</div>
      <div className="ifr-indicador__rotulo">{rotulo}</div>
    </div>
  )
}
